/**
 * Priority Values — the 12 physical value cards that ship with ValuesIncubator+.
 *
 * WHY A STATIC TS FILE (AND NOT cms_media)
 * ----------------------------------------
 * The 12 cards are a PRINTED PRODUCT, not CMS content: the artwork is a single
 * fixed design (one colour per value, one icon, one bilingual slogan) that is
 * manufactured as a deck. There is nothing for an editor to change per page,
 * and the file names are stable — so they live in `public/products/priority-values/`
 * and are referenced by path, the same way `public/products/smart-cindy/` is.
 *
 * The PRINT MASTERS (the PNGs and the print-ready PDF the deck is made from)
 * are kept in `artwork/priority-values/` — tracked in git, never served. When
 * a card is re-printed, drop the new masters there and run
 * `npm run build:priority-values` to regenerate the served WebP/PDF.
 *
 * The `slogan` below is the ENGLISH LINE PRINTED ON THE CARD. It is read off
 * the artwork, never re-translated — so a card re-print must update this
 * string in the same change, or the page will quote a slogan the printed card
 * no longer carries.
 *
 * What IS worth keeping in code is the copy that sits NEXT to the artwork:
 * the value name in three scripts, the classroom prompt, and the card's own
 * colour (used for the glow behind the card, so the physical card and the
 * on-screen card are visibly the same object).
 *
 * If the cards ever become CMS-managed (school-specific values, editable
 * slogans), move the artwork to `cms_media` and read the names/prompts from a
 * `cms_product_pages.blocks.priorityValues` array — the component already
 * takes its data as a prop, so only the data source changes.
 */

/**
 * The 12 card slugs, as a closed set.
 *
 * A literal union (rather than `string`) so the colour tables below are
 * checked by the compiler: a card added without an ink colour, or an ink
 * colour without a card, is a type error, not a silent hole.
 */
export type PriorityValueSlug =
  | 'national-identity'
  | 'responsibility'
  | 'respect-for-others'
  | 'perseverance'
  | 'law-abidingness'
  | 'benevolence'
  | 'integrity'
  | 'commitment'
  | 'filial-piety'
  | 'unity'
  | 'diligence'
  | 'empathy'

export interface PriorityValue {
  /** 1-based number printed on the card. */
  no: number
  /** URL-safe identifier, e.g. "national-identity". */
  slug: PriorityValueSlug
  /** English value name, printed in caps on the card. */
  en: string
  /**
   * The English value name in sentence case — how the site writes it.
   *
   * `en` is the PRINTED form (the card sets its title in caps), so it
   * reads as "NATIONAL IDENTITY" wherever it is quoted. The lightbox
   * heading is a sentence of the site's own copy, so it wants
   * "National Identity". The two are the same words; only the case
   * differs, so they cannot drift apart.
   */
  enTitle: string
  /** Traditional-Chinese value name, printed under the English one. */
  zh: string
  /** Simplified-Chinese value name. */
  hans: string
  /** The English slogan printed on the card, verbatim. */
  slogan: string
  /** Suggested classroom prompt (teacher asks, students reflect). */
  promptZh: string
  promptHans: string
  promptEn: string
  /** The card's background colour, sampled from the artwork. */
  color: string
  /** WebP path under `public/products/priority-values/`. */
  image: string
}

/**
 * A value with its copy resolved for one locale.
 *
 * `name` is what the page shows as the value's name and `prompt` is the
 * classroom prompt it asks. Both follow the READER's locale — the deck
 * is bilingual, so an English reader must not be handed the Chinese
 * prompt.
 */
export interface LocalisedPriorityValue extends PriorityValue {
  /** The value name, in the active locale. */
  name: string
  /** The classroom prompt, in the active locale. */
  prompt: string
  /**
   * The value's name as printed on the card in caps, for the eyebrow
   * above the name — `null` when the reader already reads that script.
   *
   * The printed card names each value twice: "NATIONAL IDENTITY" in
   * caps, and the Chinese name under it. On a Chinese page the caps
   * line is the cross-reference to the card the reader is holding. On
   * the English page the caps line IS the name, so repeating it above
   * the heading would say the same thing twice.
   */
  printedName: string | null
}

/**
 * Resolve the deck's copy for a locale.
 *
 * The three locales are the site's own: `zh-Hant`, `zh-Hans` and `en`.
 * Anything else falls back to Traditional Chinese, which is the locale
 * the cards were designed in and the site's default.
 */
export function localisePriorityValues(
  values: PriorityValue[],
  language: string,
): LocalisedPriorityValue[] {
  if (language === 'en') {
    return values.map((v) => ({
      ...v,
      name: v.enTitle,
      prompt: v.promptEn,
      printedName: null,
    }))
  }
  if (language === 'zh-Hans') {
    return values.map((v) => ({
      ...v,
      name: v.hans,
      prompt: v.promptHans,
      printedName: v.en,
    }))
  }
  return values.map((v) => ({ ...v, name: v.zh, prompt: v.promptZh, printedName: v.en }))
}

/** The WebP path of a card, under `public/products/priority-values/`. */
const card = (no: number, slug: string): string =>
  `/products/priority-values/cards/${String(no).padStart(2, '0')}-${slug}.webp`

export const PRIORITY_VALUES: PriorityValue[] = [
  {
    no: 1,
    slug: 'national-identity',
    en: 'NATIONAL IDENTITY',
    enTitle: 'National Identity',
    zh: '國民身份認同',
    hans: '国民身份认同',
    slogan: 'Pride in our roots; love our country.',
    promptZh: '你可以用哪一種方式，表達對自己出身的驕傲？',
    promptHans: '你可以用哪一种方式，表达对自己出身的骄傲？',
    promptEn: 'How could you show pride in where you come from?',
    color: '#315b9d',
    image: card(1, 'national-identity'),
  },
  {
    no: 2,
    slug: 'responsibility',
    en: 'RESPONSIBILITY',
    enTitle: 'Responsibility',
    zh: '責任感',
    hans: '责任感',
    slogan: 'Do your part; own your duties.',
    promptZh: '今天有哪一件事本來可以交給別人，你卻選擇了負起責任？',
    promptHans: '今天有哪一件事本来可以交给别人，你却选择了负起责任？',
    promptEn: 'What did you take responsibility for today, when you could have passed it on?',
    color: '#bf653d',
    image: card(2, 'responsibility'),
  },
  {
    no: 3,
    slug: 'respect-for-others',
    en: 'RESPECT FOR OTHERS',
    enTitle: 'Respect for Others',
    zh: '尊重他人',
    hans: '尊重他人',
    slogan: 'Appreciate every colour of people.',
    promptZh: '今天有沒有人跟你想法不同，而你仍然聽懂了對方？',
    promptHans: '今天有没有人跟你想法不同，而你仍然听懂了对方？',
    promptEn: 'Did you understand someone today whose opinion differs from yours?',
    color: '#38856f',
    image: card(3, 'respect-for-others'),
  },
  {
    no:  4,
    slug: 'perseverance',
    en: 'PERSEVERANCE',
    enTitle: 'Perseverance',
    zh: '堅毅',
    hans: '坚毅',
    slogan: "Don't give up; you're stronger than you think.",
    promptZh: '哪一件事你失敗過幾次仍願意再試？最後你做对了甚麼？',
    promptHans: '哪一件事你失败过几次仍愿意再试？最后你做对了什么？',
    promptEn: 'What have you kept trying at, even after failing? What finally worked?',
    color: '#5b4570',
    image: card(4, 'perseverance'),
  },
  {
    no: 5,
    slug: 'law-abidingness',
    en: 'LAW-ABIDINGNESS',
    enTitle: 'Law-abidingness',
    zh: '守法',
    hans: '守法',
    slogan: 'Obey the law; protect each other.',
    promptZh: '遵守規則的時候，你有沒有想過它保護了誰？',
    promptHans: '遵守规则的时候，你有没有想过它保护了谁？',
    promptEn: 'When you followed a rule, did you stop to think about who it protects?',
    color: '#ca8c2c',
    image: card(5, 'law-abidingness'),
  },
  {
    no: 6,
    slug: 'benevolence',
    en: 'BENEVOLENCE',
    enTitle: 'Benevolence',
    zh: '仁愛',
    hans: '仁爱',
    slogan: 'Small kindnesses light up the world.',
    promptZh: '今天你做過甚麼小事，讓身邊的人感到被關心？',
    promptHans: '今天你做过什么小事，让身边的人感到被关心？',
    promptEn: 'What small thing did you do today that made someone feel cared for?',
    color: '#f19074',
    image: card(6, 'benevolence'),
  },
  {
    no: 7,
    slug: 'integrity',
    en: 'INTEGRITY',
    enTitle: 'Integrity',
    zh: '誠信',
    hans: '诚信',
    slogan: 'Keep your word; honesty is gold.',
    promptZh: '有沒有一個你答應過別人、但想過要放棄的事？你如何守住承諾？',
    promptHans: '有没有一个你答应过别人、但想过要放弃的事？你如何守住承诺？',
    promptEn: 'Have you ever been tempted to break a promise? How did you keep it?',
    color: '#e25e6e',
    image: card(7, 'integrity'),
  },
  {
    no: 8,
    slug: 'commitment',
    en: 'COMMITMENT',
    enTitle: 'Commitment',
    zh: '承擔精神',
    hans: '承担精神',
    slogan: 'Stand up; follow through.',
    promptZh: '當一件事出了問題，你第一個反應是推給別人，還是站出來？',
    promptHans: '当一件事出了问题，你第一个反应是推给别人，还是站出来？',
    promptEn: 'When something went wrong, did you step up — or point at someone else?',
    color: '#1f90a6',
    image: card(8, 'commitment'),
  },
  {
    no: 9,
    slug: 'filial-piety',
    en: 'FILIAL PIETY',
    enTitle: 'Filial Piety',
    zh: '孝親',
    hans: '孝亲',
    slogan: 'Honour parents; repay with love.',
    promptZh: '今天你向爸媽表達過的關心，是用哪一句話說出來的？',
    promptHans: '今天你向爸妈表达过的关心，是用哪一句话说出来的？',
    promptEn: 'How did you show your parents you care today?',
    color: '#364e7a',
    image: card(9, 'filial-piety'),
  },
  {
    no: 10,
    slug: 'unity',
    en: 'UNITY',
    enTitle: 'Unity',
    zh: '團結',
    hans: '团结',
    slogan: 'Support each other; walk together.',
    promptZh: '班裡有誰今天需要你拉一把？你是怎樣做到的？',
    promptHans: '班里谁今天需要你拉一把？你是怎样做到的？',
    promptEn: 'Who in your class needed a hand today? How did you help?',
    color: '#ea6463',
    image: card(10, 'unity'),
  },
  {
    no: 11,
    slug: 'diligence',
    en: 'DILIGENCE',
    enTitle: 'Diligence',
    zh: '勤勞',
    hans: '勤劳',
    slogan: "Today's effort grows tomorrow's fruit.",
    promptZh: '你今天用上一分鐘的努力，會在明天長出甚麼果實？',
    promptHans: '你今天用上一分钟的努力，会在明天长出什么果实？',
    promptEn: 'What fruit will one minute of effort today grow tomorrow?',
    color: '#948abb',
    image: card(11, 'diligence'),
  },
  {
    no: 12,
    slug: 'empathy',
    en: 'EMPATHY',
    enTitle: 'Empathy',
    zh: '同理心',
    hans: '同理心',
    slogan: 'Listen first; stand in their shoes.',
    promptZh: '你上一次真正聽完別人說話，是甚麼時候？對方最後怎麼樣？',
    promptHans: '你上一次真正听完别人说话，是什么时候？对方最后怎么样？',
    promptEn: 'When did you last really listen to someone? What happened next?',
    color: '#6fb7ad',
    image: card(12, 'empathy'),
  },
]

/** The printable sheet — all 12 cards on one A4 landscape page (4 per page). */
export const PRIORITY_VALUES_SHEET = '/products/priority-values/priority-values-12-sheet.webp'

/** The print-ready PDF (4 cards per A4 landscape page, plus the overview). */
export const PRIORITY_VALUES_PDF = '/products/priority-values/YADA-Priority-Values-12-Cards.pdf'

/* ============================================================
   The deck's colour system
   ------------------------------------------------------------
   The twelve printed colours are chosen to be VIVID on a printed
   card, not to be legible as small text on a cream web page. Seven
   of them — gold #ca8c2c, coral #f19074, mint #6fb7ad, lilac
   #948abb, rose #e25e6e, red #ea6463 and cyan #1f90a6 — sit below
   4.5:1 against the site's cream surface.

   So each colour has TWO forms:

     • the PRINTED form — vivid, used for fills, chips and the
       colour-code stripe. It is the card as the customer sees it.
     • the INK form — the same hue mixed into the brand ink
       (#331A20) until it clears 4.5:1 on cream AND carries white
       text. The hue survives the mix, so a gold rule still reads
       gold and a mint chip still reads mint; it simply reads
       QUIETLY, the way an accent should.

   The ink values are not hand-picked: each is `printed × p` mixed with
   the brand ink, with the smallest `p` that reaches 4.5:1. They are
   hard-coded because CSS needs them as literals, and
   `scripts/lint-deck-colors.ts` re-proves every one of them (and that
   the stylesheet declares the same numbers) so the two cannot drift.
   ============================================================ */

/**
 * The ink-safe form of every card colour, keyed by slug.
 *
 * Type-checked against {@link PriorityValueSlug}: a new card without an
 * ink colour fails the build.
 */
export const PRIORITY_VALUE_INK: Record<PriorityValueSlug, string> = {
  'national-identity': '#315b9d',
  responsibility: '#ad5b39',
  'respect-for-others': '#387e6a',
  perseverance: '#5b4570',
  'law-abidingness': '#996728',
  benevolence: '#a36052',
  integrity: '#ba4e5c',
  commitment: '#227c8f',
  'filial-piety': '#364e7a',
  unity: '#ba5051',
  diligence: '#796b90',
  empathy: '#577875',
}

/** The printed colour of a card, by slug. */
export function hueFor(slug: PriorityValueSlug): string {
  const card = PRIORITY_VALUES.find((v) => v.slug === slug)
  return card?.color ?? '#315b9d'
}

/** The ink-safe colour of a card, by slug. */
export function inkFor(slug: PriorityValueSlug): string {
  return PRIORITY_VALUE_INK[slug]
}

/**
 * WHICH VALUE each section of the ValuesIncubator+ page speaks in.
 *
 * The page is not a generic product page with a colourful accent: it
 * is the 12 values laid out. Each section borrows ONE value's colour,
 * and the borrowing is meaningful rather than arbitrary — the module
 * that awards a reward card for what a student actually did is
 * RESPONSIBILITY's colour, the module that links families into the
 * school is UNITY's, and the section that promises something to every
 * stakeholder is EMPATHY's.
 *
 * `rewards` / `admin` / `analytics` / `community` are the CMS module
 * ids (see cms_product_pages.blocks.modules[*].id). A module id that
 * is not listed here falls back to the deck's order, so the page keeps
 * its rhythm even if the CMS grows a new module.
 */
export const VI_SECTION_ACCENTS = {
  /** The lead paragraph — the school's identity. */
  lead: 'national-identity',
  /** 模組一 動態獎勵卡系統 — doing your part earns the card. */
  rewards: 'responsibility',
  /** 模組二 教師與行政管理工具 — the work that has to be sustained. */
  admin: 'perseverance',
  /** 模組三 數據分析與 AI 報告 — the evidence that effort grew fruit. */
  analytics: 'diligence',
  /** 模組四 家校社群連結 — walking together. */
  community: 'unity',
  /** 系統核心特色 — standing up and following through. */
  coreFeatures: 'commitment',
  /** 對每一位持份者的承諾 — standing in their shoes. */
  stakeholders: 'empathy',
} as const satisfies Record<string, PriorityValueSlug>

/** The section keys above, in the order they appear on the page. */
export const VI_SECTION_ACCENT_ORDER = [
  'lead',
  'rewards',
  'admin',
  'analytics',
  'community',
  'coreFeatures',
  'stakeholders',
] as const

/**
 * The colour a section speaks in, given its key.
 *
 * Falls back to the deck's own order for a key that is not listed, so
 * an unknown section still gets a colour that belongs to the deck.
 */
export function accentFor(key: string): { hue: string; ink: string } {
  const known = (VI_SECTION_ACCENTS as Record<string, PriorityValueSlug>)[key]
  const slug: PriorityValueSlug = known ?? PRIORITY_VALUES[0].slug
  return { hue: hueFor(slug), ink: inkFor(slug) }
}

/**
 * The accent for a CMS module, by its id.
 *
 * A module id that is not in {@link VI_SECTION_ACCENTS} takes the next
 * unused value in deck order, so the four modules never collide and
 * the sequence still walks the deck.
 */
export function accentForModule(id: string, index: number): { hue: string; ink: string } {
  const known = (VI_SECTION_ACCENTS as Record<string, PriorityValueSlug>)[id]
  if (known) return { hue: hueFor(known), ink: inkFor(known) }
  const fallback = PRIORITY_VALUES[index % PRIORITY_VALUES.length].slug
  return { hue: hueFor(fallback), ink: inkFor(fallback) }
}
